import { create } from 'zustand';
import { DB } from '@services/database.service';
import { useUIStore } from './useUIStore';
import { useHistoryStore } from './useHistoryStore';
import { EventManager } from '../../services/event-manager.service';

interface CalendarState {
    calendarEvents: any[];
    isCalendarLoading: boolean;
}

interface CalendarActions {
    fetchCalendarEvents: (startDate: string, endDate: string) => Promise<void>;
    rescheduleEvent: (eventId: string, newDate: string, extraPayload?: any) => Promise<void>;
    rescheduleQuick: (eventId: string, type: 'tomorrow' | 'next_week') => Promise<void>;
}

/**
 * CALENDAR STORE (v4)
 * Manages grid-based calendar events and navigation.
 * HARDENED: Rescheduling logic unified via EventManager for optimistic UI.
 */
export const useCalendarStore = create<CalendarState & CalendarActions>((set, get) => ({
    calendarEvents: [],
    isCalendarLoading: false,

    fetchCalendarEvents: async (startDate, endDate) => {
        set({ isCalendarLoading: true });
        try {
            const { data } = await DB.fetchCalendarEvents(startDate, endDate);
            set({ calendarEvents: data || [] });
        } catch (err) {
            console.error("[CalendarStore] fetchCalendarEvents FAILED:", err);
        } finally {
            set({ isCalendarLoading: false });
        }
    },

    rescheduleEvent: async (eventId, newDate) => {
        useUIStore.getState().setIsLoading(true);
        try {
            await EventManager.rescheduleEvent(eventId, newDate);
        } catch (err) {
            console.error("[CalendarStore] rescheduleEvent FAILED:", err);
            throw err;
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },

    rescheduleQuick: async (eventId, type) => {
        const date = new Date();
        if (type === 'tomorrow') date.setDate(date.getDate() + 1);
        if (type === 'next_week') date.setDate(date.getDate() + 7);
        return await get().rescheduleEvent(eventId, date.toISOString().split('T')[0]);
    }
}));
